import { useEffect, useMemo, useRef, useState, lazy, Suspense } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Play, Pause, MapPin, Trophy, CalendarDays, Landmark, Search, X, Globe2, Link2, Share2, Check, Plane, Footprints, Route, Square, SkipForward } from "lucide-react";
import { api, formatDate, imgUrl } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";
import { SuggestClubDialog } from "@/components/SuggestClubDialog";

const Globe = lazy(() => import("react-globe.gl"));
const STAGE1 = 900, STAGE2 = 2600;
const STADIUM_ALT = 0.0006; // as low as the camera can go — the pitch fills the frame
const CITY_ALT = 0.015;     // pins we only know by town hover at city level
const TOUR_DWELL = 7000;    // ms per stadium on the tour (incl. ~1.8s flight)

export const ClubGlobe = ({ show }) => {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [size, setSize] = useState({ w: 600, h: 520 });
  const wrapRef = useRef(null);
  const globeRef = useRef(null);
  const sectionRef = useRef(null);
  const { current, playing, play } = usePlayer();
  const [params, setParams] = useSearchParams();
  const [ready, setReady] = useState(false);
  const tiles = true;
  const [landed, setLanded] = useState(null);
  const [streetView, setStreetView] = useState(false);
  const [sv, setSv] = useState(null);
  const flightRef = useRef({ id: null, until: 0 });
  const tourRef = useRef(false);
  const [tourIdx, setTourIdx] = useState(null);
  const initialEpRef = useRef(current?.id);

  useEffect(() => { api.get(`/shows/${show.slug}/club-map`).then((r) => setData(r.data)).catch(() => setData({ pins: [] })); }, [show.slug]);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setSize({ w: el.clientWidth, h: Math.max(460, Math.min(680, Math.round(el.clientWidth * 0.78))) }));
    ro.observe(el);
    return () => ro.disconnect();
  }, [data]);
  useEffect(() => {
    const g = globeRef.current;
    if (!g || !ready) return;
    g.controls().autoRotate = !selected;
    g.controls().autoRotateSpeed = 0.6;
    g.controls().enableZoom = true;
    g.controls().zoomSpeed = 1.4;
    if (selected && flightRef.current.id !== selected.id) { flightRef.current = { id: null, until: 0 }; setLanded(null); setStreetView(false); g.pointOfView({ lat: selected.latitude, lng: selected.longitude, altitude: tourRef.current ? 0.18 : 0.3 }, tourRef.current ? 1800 : 1100); }
  }, [selected, ready]);
  // satellite imagery from the very first frame; tiles keep sharpening the deeper you zoom
  const onZoom = ({ altitude }) => {
    if (flightRef.current.id && altitude > 0.25 && Date.now() > flightRef.current.until) { flightRef.current = { id: null, until: 0 }; setLanded(null); setStreetView(false); }
  };

  // cinematic two-stage flight: pull back over the pin, then dive onto the stadium (or hover over the city when we only know the town)
  const flyIn = (p) => {
    const g = globeRef.current;
    if (!g || !ready) return;
    const close = p.geo_precision === "stadium";
    flightRef.current = { id: p.id, until: Date.now() + STAGE1 + STAGE2 + 400 };
    setTourIdx(null);
    setSelected(p);
    setStreetView(false);
    g.controls().autoRotate = false;
    g.pointOfView({ lat: p.latitude, lng: p.longitude, altitude: 0.7 }, STAGE1);
    setTimeout(() => { if (globeRef.current && flightRef.current.id === p.id) { globeRef.current.pointOfView({ lat: p.latitude, lng: p.longitude, altitude: close ? STADIUM_ALT : CITY_ALT }, STAGE2); setLanded({ pin: p, close }); } }, STAGE1 + 50);
    // once the satellite dive finishes, drop into Google Street View — but only if Google actually has a panorama near the ground (otherwise we stay on the satellite view)
    if (close && data?.street_view_key) {
      const lookup = api.get("/club-map/street-view", { params: { lat: p.latitude, lng: p.longitude } }).then((r) => r.data).catch(() => ({ available: null }));
      setSv({ id: p.id, available: null, pano_id: null });
      lookup.then((res) => { if (flightRef.current.id === p.id) setSv({ id: p.id, ...res }); });
      setTimeout(async () => { const res = await lookup; if (flightRef.current.id === p.id && res.available !== false) setStreetView(true); }, STAGE1 + STAGE2 + 300);
    }
  };
  const flyOut = () => {
    const p = selected;
    flightRef.current = { id: null, until: 0 };
    setLanded(null);
    setStreetView(false);
    if (p) globeRef.current?.pointOfView({ lat: p.latitude, lng: p.longitude, altitude: 0.3 }, 1500);
  };

  const pins = useMemo(() => (data?.pins || []).filter((p) => p.latitude != null && p.longitude != null), [data]);
  // pressing Play anywhere on the page (episode list, card) on an episode that has a pin flies the globe onto its ground
  useEffect(() => {
    if (!playing || !current || !ready || current.id === initialEpRef.current) return;
    const p = pins.find((x) => x.episode?.id === current.id);
    if (!p || flightRef.current.id === p.id) return;
    flyIn(p);
    sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id, playing, ready, pins]);
  useEffect(() => {
    const id = params.get("club");
    if (!id || !pins.length) return;
    const p = pins.find((x) => x.id === id);
    if (p) { setSelected(p); setTimeout(() => sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 300); }
    setParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pins]);
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? pins.filter((p) => [p.club_name, p.city, p.country, p.current_league].join(" ").toLowerCase().includes(q)) : [];
  }, [pins, query]);

  // Tour: hop stadium to stadium in nearest-neighbour order so the globe glides instead of ping-ponging across oceans
  const tourOrder = useMemo(() => {
    const left = [...pins];
    const out = [];
    let cur = left.shift();
    while (cur) {
      out.push(cur);
      let best = -1, bestD = Infinity;
      left.forEach((p, i) => { const d = Math.hypot(p.latitude - cur.latitude, ((p.longitude - cur.longitude + 540) % 360) - 180); if (d < bestD) { bestD = d; best = i; } });
      cur = best >= 0 ? left.splice(best, 1)[0] : null;
    }
    return out;
  }, [pins]);
  const touring = tourIdx != null;
  tourRef.current = touring;
  const stopTour = () => setTourIdx(null);
  useEffect(() => {
    if (tourIdx == null || !tourOrder.length) return;
    flightRef.current = { id: null, until: 0 };
    setLanded(null);
    setStreetView(false);
    setSelected(tourOrder[tourIdx % tourOrder.length]);
    const t = setTimeout(() => setTourIdx((i) => (i == null ? null : i + 1)), TOUR_DWELL);
    return () => clearTimeout(t);
  }, [tourIdx, tourOrder]);
  const pick = (p) => { stopTour(); setSelected(p); };

  if (!data) return <div className="mt-14 h-[420px] rounded-3xl bg-white/5 animate-pulse" data-testid="club-globe-loading" />;
  if (pins.length === 0) return (
    <section className="mt-14 card-surface rounded-3xl p-8 flex items-center gap-4" data-testid="club-globe-pending">
      <Globe2 size={28} className="text-sky-400 shrink-0 animate-pulse" />
      <div><p className="font-display text-lg font-bold">The club atlas is being drawn</p><p className="text-sm text-slate-400">We're pinning every club from the archive onto the globe — check back in a few minutes.</p></div>
    </section>
  );
  const isCurrent = (p) => p.episode && current?.id === p.episode.id;

  return (
    <section ref={sectionRef} className="mt-14 scroll-mt-24" data-testid="club-globe-section">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <p className="eyebrow flex items-center gap-2"><Globe2 size={12} /> The club atlas</p>
          <h2 className="mt-1 font-display text-2xl sm:text-3xl font-extrabold" data-testid="club-globe-title">{pins.length} clubs and counting</h2>
          <p className="mt-1 text-sm text-slate-400">Every club we've covered, pinned where it plays. Drag to spin, scroll to zoom, tap a pin to hear its story.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => (touring ? stopTour() : setTourIdx(0))} className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-colors ${touring ? "bg-rose-500 text-white hover:bg-rose-400" : "border border-sky-400/40 text-sky-300 hover:bg-sky-400/10"}`} data-testid="club-globe-tour-toggle">
            {touring ? <><Square size={13} fill="currentColor" /> Stop tour</> : <><Route size={15} /> Tour the clubs</>}
          </button>
          <SuggestClubDialog show={show} />
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-5 items-start">
        <div ref={wrapRef} className="relative rounded-3xl overflow-hidden ring-1 ring-white/10 bg-[#03070F]" style={{ height: size.h }} data-testid="club-globe-canvas">
          <div className="absolute inset-0" onPointerDownCapture={() => touring && stopTour()}>
          <Suspense fallback={<div className="absolute inset-0 grid place-items-center text-sm text-slate-400">Loading globe…</div>}>
            <Globe
              ref={globeRef}
              width={size.w}
              height={size.h}
              backgroundColor="rgba(0,0,0,0)"
              globeImageUrl="//unpkg.com/three-globe/example/img/earth-blue-marble.jpg"
              globeTileEngineUrl={tiles ? (x, y, l) => `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${l}/${y}/${x}` : null}
              globeTileEngineMaxLevel={19}
              onGlobeReady={() => { setReady(true); globeRef.current?.pointOfView({ lat: 25, lng: 0, altitude: 1.9 }, 0); }}
              onZoom={onZoom}
              showAtmosphere
              atmosphereColor="#38bdf8"
              atmosphereAltitude={0.16}
              htmlElementsData={pins}
              htmlLat="latitude"
              htmlLng="longitude"
              htmlAltitude={0}
              htmlTransitionDuration={0}
              htmlElement={(p) => markerFor(p, selected?.id === p.id, isCurrent(p), () => pick(p))}
              ringsData={selected ? [selected] : []}
              ringLat="latitude"
              ringLng="longitude"
              ringAltitude={0.003}
              ringColor={() => (t) => `rgba(251,191,36,${0.9 - t})`}
              ringMaxRadius={2.5}
              ringPropagationSpeed={1.5}
              ringRepeatPeriod={1000}
            />
          </Suspense>
          </div>
          <p className={`absolute bottom-2 right-3 text-[10px] text-slate-500 pointer-events-none transition-opacity ${tiles ? "opacity-100" : "opacity-0"}`}>Imagery © <a href="https://www.esri.com/" className="pointer-events-auto hover:text-slate-300" target="_blank" rel="noreferrer">Esri</a>, Maxar, Earthstar Geographics</p>
          {touring && (
            <div className="absolute left-3 bottom-3 z-20 flex items-center gap-2 rounded-full bg-[#0B1A33]/90 backdrop-blur border border-sky-400/30 pl-3 pr-1.5 py-1.5 text-xs reveal" data-testid="club-globe-tour-hud">
              <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sky-400 opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-sky-400" /></span>
              <span className="text-slate-200"><span className="font-semibold">Touring</span> · <span data-testid="club-globe-tour-progress">{(tourIdx % tourOrder.length) + 1} of {tourOrder.length}</span></span>
              <button type="button" onClick={() => setTourIdx((i) => i + 1)} className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 font-semibold hover:bg-white/20" data-testid="club-globe-tour-next"><SkipForward size={12} /> Next</button>
              <button type="button" onClick={stopTour} className="rounded-full bg-white/10 px-2.5 py-1 font-semibold hover:bg-white/20" data-testid="club-globe-tour-stop">Stop</button>
            </div>
          )}
          {landed && (
            <div className="absolute left-3 bottom-3 z-20 flex items-center gap-2 rounded-full bg-[#0B1A33]/90 backdrop-blur border border-amber-400/30 pl-3 pr-1.5 py-1.5 text-xs reveal" data-testid="club-globe-landing">
              <Plane size={13} className="text-amber-400 shrink-0" />
              <span className="text-slate-200 truncate max-w-[220px]"><span className="font-semibold">{landed.close ? (landed.pin.stadium || landed.pin.club_name) : landed.pin.city || landed.pin.club_name}</span>{landed.close ? (streetView ? " · street view" : " · satellite view") : " · city view"}</span>
              {landed.close && data.street_view_key && (sv?.id === landed.pin.id && sv.available === false
                ? <span className="rounded-full bg-white/5 px-2.5 py-1 text-slate-400" data-testid="club-globe-street-view-none">No Street View here</span>
                : <button type="button" onClick={() => setStreetView((v) => !v)} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-semibold ${streetView ? "bg-amber-400 text-[#070F1E] hover:bg-amber-300" : "bg-white/10 hover:bg-white/20"}`} data-testid="club-globe-street-view-toggle"><Footprints size={12} /> {streetView ? "Satellite" : "Street View"}</button>)}
              <button type="button" onClick={flyOut} className="rounded-full bg-white/10 px-2.5 py-1 font-semibold hover:bg-white/20" data-testid="club-globe-fly-out">Fly back out</button>
            </div>
          )}
          {streetView && landed && (
            <div className="absolute inset-0 z-10 bg-[#03070F] reveal" data-testid="club-globe-street-view">
              <iframe
                title={`Street View · ${landed.pin.stadium || landed.pin.club_name}`}
                src={`https://www.google.com/maps/embed/v1/streetview?${new URLSearchParams({ key: data.street_view_key, ...(sv?.id === landed.pin.id && sv.pano_id ? { pano: sv.pano_id } : { location: `${landed.pin.latitude},${landed.pin.longitude}` }), fov: "90", pitch: "5" })}`}
                className="h-full w-full border-0"
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="club-globe-street-view-frame"
              />
              <div className="absolute left-3 top-3 flex items-center gap-2">
                <span className="rounded-full bg-[#0B1A33]/90 backdrop-blur border border-white/10 px-3 py-1.5 text-xs text-slate-200"><span className="font-semibold">{landed.pin.stadium || landed.pin.club_name}</span> · Street View</span>
                <a href={`https://www.google.com/maps?layer=c&cbll=${landed.pin.latitude},${landed.pin.longitude}`} target="_blank" rel="noreferrer" className="rounded-full bg-[#0B1A33]/90 backdrop-blur border border-white/10 px-3 py-1.5 text-xs text-slate-300 hover:text-white" data-testid="club-globe-street-view-external">Open in Google Maps</a>
              </div>
              <button type="button" onClick={() => setStreetView(false)} className="absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-[#0B1A33]/90 backdrop-blur border border-white/10 px-3 py-1.5 text-xs font-semibold text-slate-200 hover:bg-white/10" data-testid="club-globe-street-view-close"><X size={13} /> Back to satellite</button>
            </div>
          )}
          <div className="absolute left-3 top-3 right-3 sm:right-auto sm:w-72">
            <div className="flex items-center gap-2 rounded-full bg-[#0B1A33]/90 backdrop-blur border border-white/10 px-3 py-1.5">
              <Search size={14} className="text-slate-400 shrink-0" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a club, city or league…" className="bg-transparent text-sm outline-none flex-1 min-w-0 placeholder:text-slate-500" data-testid="club-globe-search" />
              {query && <button type="button" onClick={() => setQuery("")} className="text-slate-400 hover:text-white"><X size={14} /></button>}
            </div>
            {matches.length > 0 && (
              <ul className="mt-1.5 max-h-56 overflow-auto rounded-2xl bg-[#0B1A33]/95 backdrop-blur border border-white/10 p-1 text-sm" data-testid="club-globe-results">
                {matches.slice(0, 8).map((p) => <li key={p.id}><button type="button" onClick={() => { pick(p); setQuery(""); }} className="w-full text-left rounded-xl px-3 py-2 hover:bg-white/10"><span className="font-semibold">{p.club_name}</span> <span className="text-slate-400">· {[p.city, p.country].filter(Boolean).join(", ")}</span></button></li>)}
              </ul>
            )}
          </div>
        </div>

        <ClubCard pin={selected} show={show} count={pins.length} onClose={() => { stopTour(); flightRef.current = { id: null, until: 0 }; setLanded(null); setStreetView(false); setSelected(null); }} playing={selected && isCurrent(selected) && playing} onPlay={() => { if (!selected?.episode) return; if (!(isCurrent(selected) && playing)) flyIn(selected); play({ ...selected.episode, show: { name: show.name, slug: show.slug, cover_url: show.cover_url } }); }} />
      </div>
    </section>
  );
};

const markerFor = (p, active, playingNow, onClick) => {
  // zero-size anchor sits exactly on the coordinate; the pin hangs above it so its tip touches the ground
  const anchor = document.createElement("div");
  anchor.className = "club-anchor";
  const el = document.createElement("button");
  el.type = "button";
  el.className = "club-marker" + (active ? " is-active" : "") + (playingNow ? " is-playing" : "");
  el.setAttribute("aria-label", p.club_name);
  el.setAttribute("data-testid", `club-marker-${p.id}`);
  el.replaceChildren();
  if (p.logo_url) {
    const crest = document.createElement("span");
    crest.className = "club-marker-crest";
    const img = document.createElement("img");
    img.src = imgUrl(p.logo_url);
    img.alt = "";
    img.loading = "lazy";
    img.onerror = () => crest.classList.add("no-img");
    crest.appendChild(img);
    el.appendChild(crest);
  } else {
    const dot = document.createElement("span");
    dot.className = "club-marker-dot";
    el.appendChild(dot);
  }
  const tip = document.createElement("span");
  tip.className = "club-marker-tip";
  el.appendChild(tip);
  el.title = `${p.club_name}${p.city ? " · " + p.city : ""}`;
  el.onclick = (e) => { e.stopPropagation(); onClick(); };
  anchor.appendChild(el);
  return anchor;
};

const Fact = ({ icon: Icon, label, value, testId }) => value ? (
  <div className="flex items-start gap-2.5"><Icon size={15} className="mt-0.5 shrink-0 text-sky-400" /><div><p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</p><p className="text-sm text-slate-200" data-testid={testId}>{value}</p></div></div>
) : null;

const shareUrl = (pin) => `${process.env.REACT_APP_BACKEND_URL}/api/share/clubs/${pin.id}`;
const shareText = (pin, show) => `We covered ${pin.club_name} on ${show.name} 🎙️`;

const ShareRow = ({ pin, show }) => {
  const [copied, setCopied] = useState(false);
  const url = shareUrl(pin);
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); toast.success("Link copied — paste it anywhere"); setTimeout(() => setCopied(false), 2000); }
    catch { window.prompt("Copy this link", url); }
  };
  const native = async () => {
    if (navigator.share) { try { await navigator.share({ title: `${pin.club_name} · ${show.name}`, text: shareText(pin, show), url }); } catch {} }
    else copy();
  };
  const x = `https://twitter.com/intent/tweet?${new URLSearchParams({ text: shareText(pin, show), url })}`;
  const fb = `https://www.facebook.com/sharer/sharer.php?${new URLSearchParams({ u: url })}`;
  return (
    <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="club-card-share">
      <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mr-1">Share this pin</span>
      <button type="button" onClick={copy} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs font-semibold hover:bg-white/10" data-testid="club-card-copy-link">{copied ? <Check size={13} className="text-emerald-400" /> : <Link2 size={13} />} {copied ? "Copied" : "Copy link"}</button>
      <a href={x} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs font-semibold hover:bg-white/10" data-testid="club-card-share-x"><svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M18.9 2H22l-7.5 8.6L23 22h-6.8l-5.3-6.9L4.8 22H1.7l8-9.2L.5 2h7l4.8 6.3L18.9 2zm-1.2 18h1.9L7.4 3.9H5.4L17.7 20z" /></svg> Post on X</a>
      <a href={fb} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs font-semibold hover:bg-white/10" data-testid="club-card-share-facebook"><svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M22 12a10 10 0 1 0-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.3c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.3v7A10 10 0 0 0 22 12z" /></svg> Facebook</a>
      <button type="button" onClick={native} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs font-semibold hover:bg-white/10 sm:hidden" data-testid="club-card-share-native"><Share2 size={13} /> More</button>
    </div>
  );
};

const ClubCard = ({ pin, show, count, onClose, playing, onPlay }) => {
  if (!pin) return (
    <aside className="card-surface rounded-3xl p-6 lg:min-h-[420px] flex flex-col justify-center text-center" data-testid="club-card-empty">
      <MapPin size={28} className="mx-auto text-sky-400" />
      <p className="mt-3 font-display text-lg font-bold">Pick a pin</p>
      <p className="mt-1 text-sm text-slate-400">Tap any glowing point on the globe to see the club's story and play its episode — {count} to explore.</p>
    </aside>
  );
  return (
    <aside className="card-surface rounded-3xl p-6 lg:min-h-[420px] flex flex-col reveal" key={pin.id} data-testid="club-card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex items-start gap-4">
          {pin.logo_url ? <img src={imgUrl(pin.logo_url)} alt={`${pin.club_name} crest`} className="h-20 w-20 shrink-0 object-contain drop-shadow-[0_6px_16px_rgba(0,0,0,.5)]" data-testid="club-card-logo" /> : <div className="h-20 w-20 shrink-0 rounded-2xl bg-[#053671] grid place-items-center font-display text-xl font-black text-sky-200" data-testid="club-card-logo-fallback">{pin.club_name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("")}</div>}
        <div className="min-w-0">
          <p className="eyebrow">{pin.status === "defunct" ? "Defunct club" : "Club"}</p>
          <h3 className="mt-1 font-display text-2xl font-extrabold leading-tight" data-testid="club-card-name">{pin.club_name}</h3>
          {pin.nickname && <p className="text-sm text-slate-400 italic">“{pin.nickname}”</p>}
        </div>
        </div>
        <button type="button" onClick={onClose} className="rounded-full p-1.5 text-slate-400 hover:bg-white/10 hover:text-white" aria-label="Close" data-testid="club-card-close"><X size={16} /></button>
      </div>
      <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-3.5">
        <Fact icon={CalendarDays} label="Founded" value={pin.founded} testId="club-card-founded" />
        <Fact icon={Trophy} label="Current league" value={pin.current_league} testId="club-card-league" />
        <Fact icon={Landmark} label="Stadium" value={pin.stadium} testId="club-card-stadium" />
        <Fact icon={MapPin} label="Location" value={[pin.city, pin.country].filter(Boolean).join(", ")} testId="club-card-location" />
      </div>
      {pin.blurb && <p className="mt-5 text-sm text-slate-300 leading-relaxed" data-testid="club-card-blurb">{pin.blurb}</p>}
      <ShareRow pin={pin} show={show} />
      {pin.episode && (
        <div className="mt-auto pt-5">
          <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Episode · {formatDate(pin.episode.published_at)}</p>
          <p className="mt-1 text-sm font-semibold leading-snug" data-testid="club-card-episode-title">{pin.episode.title}</p>
          <button type="button" onClick={onPlay} className="mt-3 inline-flex items-center gap-2 rounded-full bg-sky-400 px-5 py-2.5 text-sm font-bold text-[#070F1E] hover:bg-sky-300 transition-colors" data-testid="club-card-play">{playing ? <Pause size={15} /> : <Play size={15} />} {playing ? "Pause episode" : "Play episode"}</button>
        </div>
      )}
    </aside>
  );
};
